import type {
  AgentSuggestion,
  BuildFromSelectionsScheduleSlot,
  CategoryOptionsOutput,
} from '@travel-planner/shared/schemas';

// Flatten all four bucket arrays into a productId → AgentSuggestion map so
// preview/budget panels can resolve a schedule slot's price, currency, and
// title in O(1). Shared between PreviewStep and BudgetStep — both panels
// consume the same `options` snapshot carried through the store.
export const buildProductIndex = (
  options: CategoryOptionsOutput['options'] | null
): Map<string, AgentSuggestion> => {
  const index = new Map<string, AgentSuggestion>();
  if (!options) return index;
  for (const product of [
    ...options.activities,
    ...options.accommodation,
    ...options.dining,
    ...options.transportation,
  ]) {
    index.set(product.id, product);
  }
  return index;
};

// Discriminated total — `mixed` and `unknown` are distinct UX states:
// `mixed` means we have prices but they span currencies (show "—" + a
// "mixed currencies" badge), `unknown` means at least one product has no
// price at all (still show "—" but for a different reason).
export type SlotTotal =
  | { kind: 'amount'; cents: number; currency: string }
  | { kind: 'mixed' }
  | { kind: 'unknown' };

export const sumSlotPrices = (
  slots: ReadonlyArray<BuildFromSelectionsScheduleSlot>,
  index: Map<string, AgentSuggestion>
): SlotTotal => {
  let cents = 0;
  let currency: string | null = null;
  let anyMissing = false;
  let mixed = false;
  for (const slot of slots) {
    const product = index.get(slot.productId);
    if (!product || product.priceCents == null || product.currency == null) {
      anyMissing = true;
      continue;
    }
    cents += product.priceCents;
    if (currency == null) currency = product.currency;
    else if (currency !== product.currency) mixed = true;
  }
  if (mixed) return { kind: 'mixed' };
  if (anyMissing || currency == null) return { kind: 'unknown' };
  return { kind: 'amount', cents, currency };
};

export const formatCents = (cents: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    // Fallback when the runtime rejects an unknown currency code.
    return `${(cents / 100).toLocaleString('en-US')} ${currency}`;
  }
};

export const formatSlotTotal = (total: SlotTotal): string => {
  if (total.kind === 'amount') return formatCents(total.cents, total.currency);
  return '—';
};
